import React, { useCallback, useRef, useState } from 'react';
import {
	Component,
	ComponentTypes,
	defaultComponentDimensions,
	defaultWidth,
	IRecipient,
	OFFSET_X,
} from '../../common/componentUtils';
import { useAppStore } from '../../common/store';
import { SchemaProperty } from '../../data/interfaces/SchemaResponse';
import { DocgenDragEvent } from '../../pages/Prepare/DocgenDragEvent';
import { GhostComponent } from '../../pages/Prepare/GhostComponent';
import { useOverlayKeyDownEvents } from '../../pages/Prepare/hooks/useOverlayKeyDownEvents';
import { ESignatureAnnotationModal } from '../../pages/Prepare/SignaturePad/ESignatureAnnotationModal';
import { RenderComponents } from '../RenderComponents';
import { StyledOverlayerPage } from './OverlayerPage.styled';

interface Props {
	components: Component[];
	addComponent?: (
		x: number,
		y: number,
		pageId: string,
		componentType: ComponentTypes | null,
		groupId?: string | null
	) => Component;
	selectedComponents?: Component[];
	onComponentUpdate?: (updatedComponents: Component[], operation?: string) => void;
	selectedField?: ComponentTypes | null;
	selectedSigner?: IRecipient | null | undefined;
	onUpdateSelectedComponents?: (comp: Component[]) => void;
	onUpdateSelectedField?: (nextComponentType: ComponentTypes | null) => void;
	areFieldsEditable: boolean;
	selectedProperty?: SchemaProperty;
	pageMetadata: any;
}

export const OverlayerPage = ({
	addComponent,
	selectedComponents: componentsToEdit,
	onComponentUpdate: handleComponentUpdate,
	selectedField: selectedComponentType,
	selectedSigner,
	onUpdateSelectedComponents: setComponentsToEdit,
	onUpdateSelectedField: setSelectedComponentType,
	components,
	areFieldsEditable,
	selectedProperty,
	pageMetadata,
}: Props) => {
	const { pageNumber, pageHeight, pageWidth, maxScalePageHeight } = pageMetadata;

	const resizedComponents = useAppStore(state => state.resizedComponents);

	const [ghostPosition, setGhostComponentPosition] = useState({
		mouseX: 0,
		mouseY: 0,
		width: '0px',
		height: '0px',
		maxY: 0,
	});
	const [isMouseDown, setIsMouseDown] = useState(false);

	const [isSignatureAnotationComponentVisible, setIsSignatureAnotationComponentVisible] =
		useState(false);

	const pageRef = useRef<HTMLDivElement>(null);
	useOverlayKeyDownEvents(
		pageRef,
		componentsToEdit,
		handleComponentUpdate,
		setComponentsToEdit,
		components
	);

	const [isGhostComponentVisible, setIsGhostComponentVisible] = useState(false);
	const showGhostComponent = useCallback(() => {
		selectedComponentType && !isGhostComponentVisible && setIsGhostComponentVisible(true);
	}, [isGhostComponentVisible, selectedComponentType]);

	const hideGhostComponent = useCallback(() => {
		setIsGhostComponentVisible(false);
	}, []);

	const handleMouseDown = () => {
		setIsMouseDown(true);
	};

	const onMouseMove = event => {
		if (!selectedComponentType) {
			return;
		}
		const { clientX, clientY } = event;
		const component = resizedComponents?.get(selectedComponentType);
		const { height: defaultComponentHeight, width: defaultComponentWidth } =
			defaultComponentDimensions[selectedComponentType];

		const height = component
			? component.height * maxScalePageHeight
			: defaultComponentHeight;
		const width = component ? component.width * pageWidth : defaultComponentWidth;

		const sizeRatio = height / width;

		const { x: parentX, y: parentY } = pageRef.current?.getBoundingClientRect()!;

		const widthRatio = pageWidth / defaultWidth;
		const newWidth = width * widthRatio;
		const newHeight = newWidth * sizeRatio;

		const offset = 2;
		const maxX =
			pageWidth - (component ? newWidth / widthRatio : newWidth) - (OFFSET_X - offset);
		const maxY = pageHeight - (component ? newHeight / widthRatio : newHeight);
		const minX = 0;
		const minY = 0;

		const newClientX = clientX - parentX;
		const newClientY = clientY - parentY;

		let mouseX =
			newClientX < maxX ? (newClientX > minX ? newClientX : minX) : maxX - offset;
		let mouseY =
			newClientY < maxY ? (newClientY > minY ? newClientY : minY) : maxY - offset;

		mouseX += offset;
		mouseY += offset;

		if (Math.ceil(newClientY - newHeight) < Math.floor(maxY)) {
			setGhostComponentPosition({
				mouseX,
				mouseY,
				width: component ? width + 'px' : newWidth + 'px',
				height: component ? height + 'px' : newHeight + 'px',
				maxY,
			});
			showGhostComponent();
		} else {
			hideGhostComponent();
		}
	};

	const handleMouseUp = event => {
		if (DocgenDragEvent.isDragging()) {
			onDrop(event);
			DocgenDragEvent.clear();
		} else if (
			isGhostComponentVisible &&
			selectedComponentType &&
			isMouseDown &&
			(event.target.id as string).startsWith('overlayer-page')
		) {
			event.preventDefault();
			const { y: parentY } = pageRef.current?.getBoundingClientRect() as DOMRect;

			const newClientY =
				event.clientY - parentY + parseFloat(ghostPosition.height) < pageHeight
					? event.clientY
					: ghostPosition.maxY + parentY;

			const component = addComponent(
				event.clientX,
				newClientY,
				event.currentTarget.id,
				selectedComponentType
			);
			setComponentsToEdit([component]);

			// Unselect the component after adding a field
			setSelectedComponentType(null);

			if (!component.signatureImage) {
				if (component?.type === ComponentTypes.SignatureAnnotationComponent) {
					setIsSignatureAnotationComponentVisible(true);
				}
			}
		}
		setIsMouseDown(false);
	};

	const onDrop = event => {
		event.preventDefault();

		const component = addComponent(
			event.clientX,
			event.clientY,
			event.currentTarget.id,
			selectedComponentType
		);
		setComponentsToEdit([component]);
		// Unselect the component after adding a field
		setSelectedComponentType(null);

		if (
			component?.type === ComponentTypes.SignatureAnnotationComponent &&
			!component.signatureImage
		) {
			setIsSignatureAnotationComponentVisible(true);
		}
	};

	const closeModal = useCallback(() => {
		setIsSignatureAnotationComponentVisible(false);
	}, []);

	const deleteComponent = useCallback(() => {
		const filteredComponent = components.filter(
			comp => comp.id === componentsToEdit[0]?.id
		);
		if (filteredComponent.length > 0) {
			const component = filteredComponent[0];
			setComponentsToEdit([]);
			handleComponentUpdate([component], 'delete');
		}
	}, [components, componentsToEdit, handleComponentUpdate, setComponentsToEdit]);

	const updateComponent = useCallback(
		signatureResponse => {
			const component = components.filter(comp => comp.id === componentsToEdit[0]?.id);
			if (component.length > 0) {
				const comp: Component = component[0];
				handleComponentUpdate([
					{
						...comp,
						signatureImage: signatureResponse.image_data_raw,
						signatureText: signatureResponse.signature_text,
						signatureType: signatureResponse.signature_type,
						signatureJsonData: signatureResponse.json_data,
					},
				]);
			}
		},
		[components, componentsToEdit, handleComponentUpdate]
	);

	const openSignatureModal = useCallback(() => {
		setIsSignatureAnotationComponentVisible(true);
	}, []);

	const editableEvents = areFieldsEditable
		? {
				onMouseDown: handleMouseDown,
				onMouseUp: handleMouseUp,
				onMouseLeave: hideGhostComponent,
				onMouseMove,
		  }
		: {};

	const pageBoundingClientRect = {
		width: pageWidth,
		height: maxScalePageHeight,
		actualPageHeight: pageHeight,
	};

	return (
		<StyledOverlayerPage
			ref={pageRef}
			id={`overlayer-page-${pageNumber}`}
			data-testid={`overlayer-page-${pageNumber}`}
			{...editableEvents}
			style={{ cursor: !!selectedComponentType ? 'crosshair' : 'initial' }}
		>
			<RenderComponents
				components={components}
				page={pageNumber}
				pageBoundingClientRect={pageBoundingClientRect}
				hideGhostComponent={hideGhostComponent}
				setComponentsToEdit={setComponentsToEdit}
				handleComponentUpdate={handleComponentUpdate}
				componentsToEdit={componentsToEdit}
				openSignatureModal={openSignatureModal}
				pageHeight={pageHeight}
				areFieldsEditable={areFieldsEditable}
				addComponent={addComponent}
				resizedComponents={resizedComponents}
			/>
			{areFieldsEditable &&
				isGhostComponentVisible &&
				selectedComponentType &&
				pageBoundingClientRect && (
					<GhostComponent
						pageBoundingClientRect={pageBoundingClientRect}
						selectedComponentType={selectedComponentType}
						selectedSigner={selectedSigner}
						ghostPosition={ghostPosition}
						selectedProperty={selectedProperty}
					/>
				)}

			{areFieldsEditable && isSignatureAnotationComponentVisible && (
				<ESignatureAnnotationModal
					closeModal={closeModal}
					deleteComponent={deleteComponent}
					updateComponent={updateComponent}
					componentsToEdit={componentsToEdit}
				/>
			)}
		</StyledOverlayerPage>
	);
};
